<template>
  <CallButton
    :tooltip-message="tooltipMessage"
    :disabled="callDisabled"
    @click="handleCall"
  />
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapWaitingActions } from 'vue-wait';
import { mapGetters } from 'vuex';
import { ZoomSettingApiService } from '@/api/user/resources/zoom_setting';
import { makeCall } from '@/services/zoom_phone_service';
import CallButton from './CallButton.vue';

export default defineComponent({
  components: {
    CallButton,
  },
  props: {
    phoneNumber: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      zoomSetting: null,
    };
  },
  computed: {
    ...mapGetters('zoomPhone', [
      'isCallable',
      'isCalling',
      'hasCallInfo',
    ]),
    ...mapGetters('userUi', [
      'zoomPhoneWidgetVisible',
    ]),
    tooltipMessage () {
      if (!this.isValidPhoneNumber) {
        return this.$t('cti.invalidPhoneNumber');
      } else if (this.isCalling) {
        return this.$t('zoomPhone.busy');
      } else if (this.hasCallInfo) {
        return this.$t('zoomPhone.hasCallInfo');
      } else if (this.callDisabled) {
        return this.$t('zoomPhone.notStarted');
      } else {
        return this.$t('zoomPhone.call');
      }
    },
    isValidPhoneNumber(): boolean {
      return typeof this.phoneNumber === 'string' && this.phoneNumber.length > 0;
    },
    callDisabled(): boolean {
      if (this.disabled) return true;
      if (this.hasCallInfo) return true;

      if (!this.zoomSetting?.useZoomPhone && !this.zoomPhoneWidgetVisible) return true;
      return !this.isValidPhoneNumber || !this.isCallable;
    },
  },
  created() {
    this.fetchZoomSetting();
  },
  methods: {
    ...mapWaitingActions('userUi', {
      setZoomPhoneWidgetVisibleAction: 'setZoomPhoneWidgetVisibleWait',
    }),
    async fetchZoomSetting() {
      const api = new ZoomSettingApiService();
      const { data: { zoomSetting } } = await api.getZoomSetting();
      this.zoomSetting = zoomSetting;
    },
    async handleCall(): Promise<void> {
      if (this.callDisabled) return;

      await this.setZoomPhoneWidgetVisibleAction(true);
      makeCall(this.phoneNumber, () => {
        this.$bitterAlert.show({
          title: this.$t('zoomPhone.callFailedTitle'),
          text: this.$t('zoomPhone.callFailedText'),
        });
      });
    },
  },
});
</script>
